import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { capitalize } from "lodash";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { MultiSelectCheckbox } from "@whoosh/design-system/src/components/controls";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { CourseBucketFilterControlFragment_facility$key } from "./__generated__/CourseBucketFilterControlFragment_facility.graphql";

type Props = {
  facilityRef: CourseBucketFilterControlFragment_facility$key | null;
  selectedCourseBucketIds?: string[];
  onChange: (value: (string | null)[]) => void;
};

export const CourseBucketFilterControl = ({
  facilityRef,
  selectedCourseBucketIds = [],
  onChange,
}: Props) => {
  const facility = useFragment(
    CourseBucketFilterControlFragmentString,
    facilityRef
  );

  const courseNoun = getNounOverride(
    facility?.copyOverrides?.course?.singular,
    t`Course`
  );
  const facilityVenueLabel = capitalize(courseNoun);

  const courseBucketOptions = useMemo(() => {
    return (
      facility?.courseBuckets?.edges?.flatMap((e) =>
        e?.node
          ? [
              {
                label: e.node.name || "",
                value: e.node.id,
              },
            ]
          : []
      ) || []
    );
  }, [facility?.courseBuckets?.edges]);

  return courseBucketOptions.length > 1 ? (
    <VStack gap={4}>
      <Text variant="mainSmallSemiBold">{`${facilityVenueLabel} ${t`Type`}`}</Text>
      <MultiSelectCheckbox
        values={selectedCourseBucketIds}
        options={courseBucketOptions}
        onChange={onChange}
        placeholder={t`Any`}
      />
    </VStack>
  ) : null;
};

export const CourseBucketFilterControlFragmentString = graphql`
  fragment CourseBucketFilterControlFragment_facility on Facility {
    courseBuckets {
      edges {
        node {
          id
          name
        }
      }
    }
    copyOverrides {
      course {
        singular
      }
    }
  }
`;
